import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@material-ui/core';
import AddToHomeScreenIcon from '@material-ui/icons/AddToHomeScreen';
import CloseIcon from '@material-ui/icons/Close';
const img = require('../../../images/pwa-installation-chrome.png');

// Lightweight, reusable PWA install helper
// - Captures beforeinstallprompt and exposes an Install button
// - Falls back to a help dialog with platform-specific instructions when prompt isn't available
// - Hides itself if already installed (standalone display-mode)

function getPlatformInfo() {
  const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isAndroid = /android/.test(ua);
  const isSamsung = /samsungbrowser/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !/edg\//.test(ua) && !/opr\//.test(ua);
  const isEdge = /edg\//.test(ua);
  const isFirefox = /firefox/.test(ua);
  return { isIOS, isAndroid, isSamsung, isChrome, isEdge, isFirefox };
}

function isStandaloneDisplayMode() {
  const matchStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = typeof window.navigator.standalone === 'boolean' ? window.navigator.standalone : false;
  return Boolean(matchStandalone || iosStandalone);
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(isStandaloneDisplayMode());
  const dismissedRef = useRef(false);
  const deferredPromptRef = useRef(null);
  const DELAY_MS = 3000; // delay before showing fallback CTA

  const platform = useMemo(() => getPlatformInfo(), []);

  // Listen for PWA install events
  useEffect(() => {
    function onBeforeInstallPrompt(e) {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      setDeferredPrompt(e);
      deferredPromptRef.current = e;
      if (!dismissedRef.current) setVisible(true);
    }

    function onAppInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
      setVisible(false);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    // Delay initial banner so that if the beforeinstallprompt arrives quickly, we can show Install directly
    const revealTimer = setTimeout(() => {
      if (!isStandaloneDisplayMode() && !dismissedRef.current) {
        setVisible(true);
      }
    }, DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      clearTimeout(revealTimer);
    };
  }, []);

  // Re-check installed state on visibility changes (e.g., after refresh)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isStandaloneDisplayMode()) {
        setInstalled(true);
        setVisible(false);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [visible]);

  const promptInstall = useCallback(async () => {
    try {
      if (!deferredPrompt) {
        // No prompt available, show help instead
        setShowHelp(true);
        return;
      }
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      // If accepted, we can hide; if dismissed, keep button for later
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } catch (err) {
      // Fallback to help dialog
      setShowHelp(true);
    }
  }, [deferredPrompt]);

  const closeBanner = useCallback(() => {
    dismissedRef.current = true;
    setVisible(false);
  }, []);

  if (installed || !visible) return null;

  const showInstallButton = Boolean(deferredPrompt);
  const buttonLabel = 'Install App';

  return (
    <>
      {/* Floating install banner */}
      <div style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 1600,
        background: 'rgba(35,47,62,0.97)',
        color: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: 360
      }}>
        <AddToHomeScreenIcon style={{ color: '#61AFFE' }} />
        <Typography variant="body2" style={{ flex: 1, marginRight: 8 }}>
          Add ezybill to your home screen for a faster, full-screen experience.
        </Typography>

        {showInstallButton ? (
          <Button variant="contained" color="secondary" onClick={promptInstall} size="small">
            {buttonLabel}
          </Button>
        ) : (
          <Tooltip title="Show how to install on your device">
            <Button variant="contained" color="secondary" onClick={() => setShowHelp(true)} size="small">
              How to install
            </Button>
          </Tooltip>
        )}

        <Tooltip title="Hide">
          <IconButton size="small" onClick={closeBanner} style={{ color: '#fff' }} aria-label="Close install banner">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Help instructions dialog */}
      <Dialog open={showHelp} onClose={() => setShowHelp(false)} aria-labelledby="pwa-install-help-title">
        <DialogTitle id="pwa-install-help-title">Install this app</DialogTitle>
        <DialogContent dividers>
          {platform.isAndroid && (
            <div style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">On Android (Chrome)</Typography>
              <Typography variant="body2" color="textSecondary">
                1) Open in Chrome. 2) Tap the ⋮ menu (top right). 3) Choose "Add to Home screen" or "Install app".
              </Typography>
            </div>
          )}
          {platform.isIOS && (
            <div style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">On iPhone/iPad (Safari)</Typography>
              <Typography variant="body2" color="textSecondary">
                1) Open this site in Safari. 2) Tap the Share icon. 3) Select "Add to Home Screen".
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Note: iOS does not support the native install prompt. Use Safari to add to your Home Screen.
              </Typography>
            </div>
          )}
          {!platform.isAndroid && !platform.isIOS && (
            <div style={{ marginBottom: 8 }}>
              <Typography variant="subtitle2">On desktop (Chrome/Edge)</Typography>
              
              <img
                src={(() => {
                  try {
                    return img && img.default ? img.default : img;
                  } catch (err) {
                    // Fallback to CDN if local image is not present
                    return 'https://developer.chrome.com/docs/workbox/images/pwa-installation-chrome.png';
                  }
                })()}
                alt="Install prompt in Chrome"
                style={{ width: '100%', marginTop: 8 }}
              />
              <Typography variant="body2" color="textSecondary">
                Click the Install icon in the address bar, or open the browser menu and choose "Install App".
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {showInstallButton && (
            <Button color="secondary" onClick={promptInstall} variant="contained">
              {buttonLabel}
            </Button>
          )}
          <Button onClick={() => setShowHelp(false)} color="default" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
